<script lang="ts">
  import { service } from "@frontend/services";
  import { GetCachedToken, me } from "@frontend/user";
  import { push } from "svelte-spa-router";
  import { onMount } from "svelte";
  import { formatDate, formatDateTime, formatMoney } from "./Formats";
  import { translate, currentLanguage } from "../Translator";
  import { 
    type AuthenticatedRequestOfUserbyTransactionRequest, 
    type UserbyTransactionRequest,
    type ApiResultOfPaginatedUserbyTransactionResponse,
    type PaginatedUserbyTransactionResponse,
    type UserbyTransactionResponse,
    type PromotionDetail
  } from "@frontend/walletapi";
  

  type ViewAction = {
    action: "view",
    userid: number;
  };

  type RouteParams = ViewAction | undefined;
  
  export let params: RouteParams = undefined;

  let transactions: UserbyTransactionResponse[] = [];
  let loading = false;
  let error: string | null = null;
  let userId: number | null = null;
  let showPromotionModal = false;
  let selectedTransaction: UserbyTransactionResponse | null = null;
  
  // Paginación
  let currentPage = 1;
  let itemsPerPage = 30;
  let totalPages = 1;

  onMount(async () => {
    // Verificar autenticación primero
    if (!$me) {
      push("/user/Login");
      return;
    }

    if (params?.action === "view" && params.userid) {
      userId = params.userid;
      await loadTransactions();
    }
  });

  async function loadTransactions(page: number = 1) {
    if (!userId) return;

    loading = true;
    error = null;

    try {
      const token = await GetCachedToken();
      const request: AuthenticatedRequestOfUserbyTransactionRequest = {
        Token: token,
        Data: {
          UserId: userId,
          Page: page,
          PageSize: itemsPerPage
        }
      };

      const response: ApiResultOfPaginatedUserbyTransactionResponse = await service.walletapi.userbytransaction(request);
      
      if (response.Success && response.Data) {
        transactions = response.Data.Items || [];
        totalPages = response.Data.TotalPages;
        currentPage = response.Data.CurrentPage;
      } else {
        error = response.ErrorMessage || "Error loading transactions";
      }
    } catch (err) {
      error = "Failed to load transactions";
      console.error("Error loading transactions:", err);
    } finally {
      loading = false;
    }
  }

  function changePage(page: number) {
    if (page >= 1 && page <= totalPages) {
      loadTransactions(page);
    }
  }

  function getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  function goBack() {
    push("/PlayerList");
  }

  function formatDateShort(date: Date): string {
    return new Date(date).toLocaleDateString();
  }

  function getPromoAmount(transaction: UserbyTransactionResponse): number {
    if (!transaction.PromotionDetails || transaction.PromotionDetails.length === 0) {
      return 0;
    }
    return transaction.PromotionDetails.reduce((total, promo) => total + (promo.Amount?.InCents || 0), 0);
  }

  function getPromoBalance(transaction: UserbyTransactionResponse): number {
    if (!transaction.PromotionDetails || transaction.PromotionDetails.length === 0) {
      return 0;
    }
    return transaction.PromotionDetails.reduce((total, promo) => total + (promo.Balance?.InCents || 0), 0);
  }

  function openPromotionModal(transaction: UserbyTransactionResponse) {
    selectedTransaction = transaction;
    showPromotionModal = true;
  }

  function closePromotionModal() {
    showPromotionModal = false;
    selectedTransaction = null;
  }
</script>

<div class="ui container">
  <div class="ui basic segment">
    <button class="ui labeled icon button" on:click={goBack}>
      <i class="arrow left icon"></i>
      {$translate('Back to Player List')}
    </button>
  </div>

  {#if loading}
    <div class="ui active inverted dimmer">
      <div class="ui text loader">{$translate('Loading')}</div>
    </div>
  {:else if error}
    <div class="ui negative message">
      <i class="close icon"></i>
      <div class="header">{$translate('Error')}</div>
      <p>{error}</p>
    </div>
  {:else if transactions.length === 0}
    <div class="ui placeholder segment">
      <div class="ui icon header">
        <i class="search icon"></i>
        {$translate('No transactions found for this user')}
      </div>
    </div>
  {:else}
    <div class="ui segment">
      <h3 class="ui header">
        <i class="list icon"></i>
        <div class="content">
          {$translate('User Transactions')}
          <div class="sub header">
            {transactions[0]?.FirstName} {transactions[0]?.LastName} ({transactions[0]?.Email})
          </div>
        </div>
      </h3>

      <table class="ui celled table">
        <thead>
          <tr>
            <th class="center aligned">{$translate('Transaction ID')}</th>
            <th class="center aligned">{$translate('Type of Transaction')}</th>
            <th class="center aligned">{$translate('Date')}</th>
            <th class="center aligned">{$translate('Cashable Amount')}</th>
            <th class="center aligned">{$translate('Cashable Balance')}</th>
            <th class="center aligned">{$translate('Money Amount')}</th>
            <th class="center aligned">{$translate('Money Balance')}</th>
            <th class="center aligned">{$translate('Promo Amount')}</th>
            <th class="center aligned">{$translate('Promo Balance')}</th>
            <th class="center aligned">{$translate('Details')}</th>
          </tr>
        </thead>
        <tbody>
          {#each transactions as transaction}
            <tr>
              <td class="center aligned" data-label="{$translate('Transaction ID')}">{transaction.WalletTransactionId}</td>
              <td class="center aligned" data-label="{$translate('Type')}">
                <span class="ui small label {
                  transaction.Type === 'Deposit' ? 'green' : 
                  transaction.Type === 'Withdraw' ? 'red' : 
                  transaction.Type === 'PromoExpired' ? 'red' : 
                  'blue'
                }">
                  {transaction.Type}
                </span>
              </td>
              <td class="center aligned date" data-label="{$translate('Date')}">{formatDateTime(transaction.TransactionTime)}</td>
              <td class="center aligned numeric" data-label="{$translate('Cashable Amount')}">
                {transaction.CashableAmount ? formatMoney(transaction.CashableAmount.InCents) : formatMoney(0)}
              </td>
              <td class="center aligned numeric" data-label="{$translate('Cashable Balance')}">
                {transaction.CashableBalance ? formatMoney(transaction.CashableBalance.InCents) : formatMoney(0)}
              </td>
              <td class="center aligned numeric" data-label="{$translate('Money Amount')}">
                {transaction.MoneyTransactionAmount ? formatMoney(transaction.MoneyTransactionAmount.InCents) : formatMoney(0)}
              </td>
              <td class="center aligned numeric" data-label="{$translate('Money Balance')}">
                {transaction.MoneyTransactionBalance ? formatMoney(transaction.MoneyTransactionBalance.InCents) : formatMoney(0)}
              </td>
              <td class="center aligned numeric" data-label="{$translate('Promo Amount')}">
                {#if getPromoAmount(transaction) > 0}
                  <div>
                    {formatMoney(getPromoAmount(transaction))}
                    {#if transaction.PromotionDetails && transaction.PromotionDetails.length > 1}
                      <div class="ui small label">
                        {transaction.PromotionDetails.length} promos
                      </div>
                    {/if}
                  </div>
                {:else}
                  {formatMoney(0)}
                {/if}
              </td>
              <td class="center aligned numeric" data-label="{$translate('Promo Balance')}">
                {getPromoBalance(transaction) > 0 ? formatMoney(getPromoBalance(transaction)) : formatMoney(0)}
              </td>
              <td class="center aligned" data-label="{$translate('Details')}">
                {#if transaction.PromotionDetails && transaction.PromotionDetails.length > 0}
                  <button class="ui small label view-button" on:click={() => openPromotionModal(transaction)}>
                    <i class="eye icon"></i>
                    {$translate('Details')}
                  </button>
                {:else}
                  <span class="ui small label">{$translate('No Promotions')}</span>
                {/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if totalPages > 1}
        <div class="ui center aligned segment">
          <div class="ui tiny pagination menu">
            <button 
              class="item {currentPage === 1 ? 'disabled' : ''}" 
              on:click={() => changePage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <i class="left chevron icon"></i>
              {$translate('Previous')}
            </button>
            
            {#each getPageNumbers() as pageNum}
              <button 
                class="item {currentPage === pageNum ? 'active' : ''}" 
                on:click={() => changePage(pageNum)}
              >
                {pageNum}
              </button>
            {/each}
            
            <button 
              class="item {currentPage === totalPages ? 'disabled' : ''}" 
              on:click={() => changePage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {$translate('Next')}
              <i class="right chevron icon"></i>
            </button>
          </div>
          
          <div class="ui tiny text">
            {$translate('Page')} {currentPage} {$translate('of')} {totalPages} 
            ({transactions.length} {$translate('of')} {totalPages * itemsPerPage} {$translate('transactions')})
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Modal de Ticket de Transacción -->
{#if showPromotionModal && selectedTransaction}
  <div class="modal-overlay" on:click={closePromotionModal}>
    <div class="ticket-modal" on:click|stopPropagation>
      <div class="header ticket-header">
        <i class="receipt icon"></i>
        {$translate('Transaction Ticket')} #{selectedTransaction.WalletTransactionId}
      </div>
      <div class="content ticket-content">
        <!-- Información del Usuario -->
        <div class="user-info">
          <div class="user-name">
            <i class="user icon"></i>
            {selectedTransaction.FirstName} {selectedTransaction.LastName}
          </div>
          <div class="user-email">
            <i class="mail icon"></i>
            {selectedTransaction.Email}
          </div>
        </div>

        <!-- Tabla de Ticket -->
        <div class="ticket-table">
          <table class="receipt-table">
            <tr>
              <td class="receipt-title">{$translate('Transaction ID')}</td>
              <td class="receipt-value">#{selectedTransaction.WalletTransactionId}</td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Type of Transaction')}</td>
              <td class="receipt-value">
                <span class="ui small label {
                  selectedTransaction.Type === 'Deposit' ? 'green' : 
                  selectedTransaction.Type === 'Withdraw' ? 'red' : 
                  selectedTransaction.Type === 'PromoExpired' ? 'red' : 
                  'blue'
                }">
                  {selectedTransaction.Type}
                </span>
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Date')}</td>
              <td class="receipt-value">{formatDateTime(selectedTransaction.TransactionTime)}</td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Amount')}</td>
              <td class="receipt-value">
                {selectedTransaction.CashableAmount ? formatMoney(selectedTransaction.CashableAmount.InCents) : formatMoney(0)}
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Total')}</td>
              <td class="receipt-value">
                {selectedTransaction.CashableAmount ? formatMoney(selectedTransaction.CashableAmount.InCents) : formatMoney(0)}
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Amount')}</td>
              <td class="receipt-value">
                {selectedTransaction.MoneyTransactionAmount ? formatMoney(selectedTransaction.MoneyTransactionAmount.InCents) : formatMoney(0)}
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Balance')}</td>
              <td class="receipt-value">
                {selectedTransaction.MoneyTransactionBalance ? formatMoney(selectedTransaction.MoneyTransactionBalance.InCents) : formatMoney(0)}
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Applied Promotion')}</td>
              <td class="receipt-value">
                {getPromoAmount(selectedTransaction) > 0 ? formatMoney(getPromoAmount(selectedTransaction)) : formatMoney(0)}
              </td>
            </tr>
            <tr>
              <td class="receipt-title">{$translate('Promotion Balance')}</td>
              <td class="receipt-value">
                {getPromoBalance(selectedTransaction) > 0 ? formatMoney(getPromoBalance(selectedTransaction)) : formatMoney(0)}
              </td>
            </tr>
          </table>
        </div>

        <!-- Detalles de Promociones -->
        {#if selectedTransaction.PromotionDetails && selectedTransaction.PromotionDetails.length > 0}
          <div class="promotions-section">
            <div class="section-header">
              <i class="gift icon"></i>
              {$translate('Promotions Details')}
            </div>
            <div class="promotions-list">
              {#each selectedTransaction.PromotionDetails as promotion, index}
                <div class="promotion-item">
                  <div class="promotion-header">
                    <span class="promotion-name">{promotion.PromotionName || 'Promoción sin nombre'}</span>
                    <span class="ui small label {promotion.CampaignType === 'Register' ? 'blue' : promotion.CampaignType === 'Recharge' ? 'green' : 'orange'}">
                      {promotion.CampaignType}
                    </span>
                  </div>
                  <div class="promotion-details">
                    <div class="promotion-amounts">
                      <span><strong>{$translate('Amount')}:</strong> {formatMoney(promotion.Amount?.InCents || 0)}</span>
                      <span><strong>{$translate('Balance')}:</strong> {formatMoney(promotion.Balance?.InCents || 0)}</span>
                      <span><strong>{selectedTransaction.Type === 'PromoExpired' ? 'PromoExpired' : 'Initial'}:</strong> {formatMoney(promotion.InitialBalance?.InCents || 0)}</span>
                    </div>
                    <div class="promotion-dates">
                      <span><i class="calendar plus icon"></i> {$translate('Start Date')}: {formatDateTime(promotion.StartDate)}</span>
                      <span><i class="calendar minus icon"></i> {$translate('Expiration Date')}: {formatDateTime(promotion.ExpirationDate)}</span>
                    </div>
                    <!-- <div class="promotion-id">
                      <span class="ui tiny label">ID: {promotion.CampaignsId}</span>
                    </div> -->
                  </div>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      </div>
      <div class="actions ticket-actions">
        <button class="ui small button" on:click={closePromotionModal}>
          <i class="close icon"></i>
         {$translate('Dismiss')}
        </button>
        <button class="ui small primary button" on:click={() => window.print()}>
          <i class="print icon"></i>
         {$translate('Print')}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  /* Centrar todo el contenido */
  .ui.container {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 1rem !important;
  }

  .ui.segment {
    width: 100% !important;
    max-width: 100% !important;
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }

  /* Centrar la tabla */
  .ui.table {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 auto !important;
    text-align: center !important;
  }

  /* Centrar todos los elementos de la tabla */
  .ui.table th,
  .ui.table td {
    text-align: center !important;
    vertical-align: middle !important;
    padding: 0.5em !important;
    font-size: 0.85em;
  }

  /* Centrar el header */
  .ui.header {
    text-align: center !important;
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
  }

  /* Centrar botones */
  .ui.button {
    text-align: center !important;
    margin: 0 auto !important;
  }

  .ui.basic.segment {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 1em;
    width: 100% !important;
  }

  /* Centrar paginación */
  .ui.center.aligned.segment {
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    width: 100% !important;
  }

  .ui.tiny.pagination.menu {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    margin: 0 auto !important;
    font-size: 0.7em !important;
  }

  .ui.tiny.text {
    text-align: center !important;
    width: 100% !important;
    font-size: 0.6em !important;
  }

  /* Centrar labels y badges */
  .ui.small.label {
    font-size: 0.75em;
    text-align: center !important;
    display: inline-block !important;
    margin: 0 auto !important;
  }

  /* Centrar valores numéricos */
  .numeric {
    font-family: monospace;
    text-align: center !important;
  }

  /* Centrar fechas */
  .ui.table td.date {
    white-space: nowrap;
    text-align: center !important;
  }

  /* Centrar botones de vista */
  .view-button {
    background-color: #2185d0 !important;
    color: white !important;
    border: none !important;
    cursor: pointer !important;
    transition: background-color 0.2s ease !important;
    font-weight: bold !important;
    text-align: center !important;
    margin: 0 auto !important;
    display: inline-block !important;
  }

  .view-button:hover {
    background-color: #1678c2 !important;
  }

  .view-button .icon {
    margin-right: 0.3em !important;
  }

  /* Modal overlay */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    box-sizing: border-box;
  }

  /* Modal principal */
  .ticket-modal {
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 100%;
    max-height: 85vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .ticket-modal .header {
    background-color: #f0f0f0;
    padding: 0.6em 1em;
    font-size: 1em;
    font-weight: bold;
    border-bottom: 2px solid #ddd;
    text-align: center !important;
  }

  .ticket-modal .header i {
    margin-right: 0.4em;
    color: #2185d0;
  }

  .ticket-modal .content {
    padding: 0.8em 1em;
    flex: 1;
    overflow-y: auto;
    background-color: #fafafa;
    text-align: center;
  }

  .ticket-modal .user-info {
    margin-bottom: 0.8em;
    padding: 0.6em;
    border-bottom: 2px solid #ddd;
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    text-align: center;
  }

  .ticket-modal .user-info .user-name {
    font-size: 1.1em;
    font-weight: bold;
    margin-bottom: 0.2em;
    color: #333;
    text-align: center !important;
  }

  .ticket-modal .user-info .user-name i {
    margin-right: 0.4em;
    color: #2185d0;
  }

  .ticket-modal .user-info .user-email {
    font-size: 0.85em;
    color: #666;
    text-align: center !important;
  }

  .ticket-modal .user-info .user-email i {
    margin-right: 0.3em;
    color: #666;
  }

  .ticket-modal .ticket-table {
    margin-bottom: 0.8em;
    padding: 0.6em;
    border-bottom: 2px solid #ddd;
    background-color: white;
    border-radius: 5px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    text-align: center;
  }

  .ticket-modal .receipt-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Courier New', monospace;
    margin: 0 auto !important;
  }

  .ticket-modal .receipt-table td {
    padding: 0.4em 0.6em;
    text-align: center !important;
    border-bottom: 1px solid #eee;
    font-size: 0.85em;
  }

  .ticket-modal .receipt-table tr:last-child td {
    border-bottom: none;
  }

  .ticket-modal .receipt-table td:first-child {
    font-weight: bold;
    color: #333;
    width: 40%;
    text-align: center !important;
  }

  .ticket-modal .receipt-table td:last-child {
    text-align: center !important;
    font-weight: bold;
    color: #2185d0;
  }

  .ticket-modal .receipt-table .receipt-title {
    background-color: #f8f9fa;
    font-weight: bold;
    text-align: center !important;
  }

  .ticket-modal .receipt-table .receipt-value {
    background-color: white;
    text-align: center !important;
  }

  .ticket-modal .promotions-section {
    margin-top: 1em;
    padding-top: 1em;
    border-top: 2px solid #ddd;
    text-align: center !important;
  }

  .ticket-modal .section-header {
    font-size: 1em;
    font-weight: bold;
    margin-bottom: 0.6em;
    padding-bottom: 0.6em;
    border-bottom: 1px solid #eee;
    color: #333;
    text-align: center !important;
  }

  .ticket-modal .section-header i {
    margin-right: 0.4em;
    color: #21ba45;
  }

  .ticket-modal .promotions-list {
    display: flex;
    flex-direction: column;
    gap: 0.6em;
    align-items: center !important;
  }

  .ticket-modal .promotion-item {
    background-color: white;
    padding: 0.6em 0.8em;
    border-radius: 5px;
    border: 1px solid #ddd;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    text-align: center !important;
    width: 100% !important;
  }

  .ticket-modal .promotion-header {
    display: flex;
    justify-content: center !important;
    align-items: center;
    margin-bottom: 0.4em;
    padding-bottom: 0.4em;
    border-bottom: 1px solid #eee;
    gap: 0.5em;
  }

  .ticket-modal .promotion-name {
    font-weight: bold;
    font-size: 0.9em;
    color: #333;
    text-align: center !important;
  }

  .ticket-modal .promotion-details {
    font-size: 0.8em;
    color: #666;
    text-align: center !important;
  }

  .ticket-modal .promotion-amounts {
    margin-bottom: 0.4em;
    display: flex;
    flex-wrap: wrap;
    gap: 0.8em;
    justify-content: center !important;
  }

  .ticket-modal .promotion-amounts span {
    background-color: #f8f9fa;
    padding: 0.15em 0.4em;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    text-align: center !important;
  }

  .ticket-modal .promotion-dates {
    margin-bottom: 0.4em;
    display: flex;
    flex-wrap: wrap;
    gap: 0.8em;
    font-size: 0.75em;
    justify-content: center !important;
  }

  .ticket-modal .promotion-dates span {
    color: #666;
    text-align: center !important;
  }

  .ticket-modal .promotion-dates i {
    margin-right: 0.2em;
    color: #999;
  }

  .ticket-modal .ticket-actions {
    display: flex;
    justify-content: center;
    gap: 0.4em;
    padding: 0.6em 1em;
    background-color: #f8f9fa;
    border-top: 1px solid #ddd;
    border-radius: 0 0 8px 8px;
    flex-shrink: 0;
  }

  .ticket-modal .ticket-actions .ui.button {
    flex: 1;
    font-weight: bold;
    font-size: 0.9em;
    text-align: center !important;
  }

  .ticket-modal .ticket-actions .ui.primary.button {
    background-color: #2185d0;
    color: white;
  }

  .ticket-modal .ticket-actions .ui.primary.button:hover {
    background-color: #1678c2;
  }


  /* Estilos para pantallas pequeñas y zoom 100% */
  @media (max-height: 800px) {
    .modal-overlay {
      padding: 10px;
    }
    
    .ticket-modal {
      max-height: 80vh;
      max-width: 400px;
    }
    
    .ticket-modal .content {
      padding: 0.6em 0.8em;
    }
    
    .ticket-modal .user-info,
    .ticket-modal .ticket-table {
      padding: 0.5em;
      margin-bottom: 0.6em;
    }
    
    .ticket-modal .ticket-actions {
      padding: 0.5em 0.8em;
    }
  }

  /* Estilos para impresión */
  @media print {
    .ticket-modal {
      position: static !important;
      width: 100% !important;
      max-width: none !important;
      box-shadow: none !important;
    }

    .ticket-modal .header {
      background-color: white !important;
      border-bottom: 2px solid black !important;
    }

    .ticket-modal .content {
      background-color: white !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .ticket-modal .user-info,
    .ticket-modal .ticket-table,
    .ticket-modal .promotion-item {
      background-color: white !important;
      box-shadow: none !important;
      border: 1px solid #ccc !important;
    }

    .ticket-modal .ticket-actions {
      display: none !important;
    }
  }

  /* Responsive styles */
  @media (max-width: 768px) {
    .ui.container {
      padding: 0.5rem !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }

    .ui.segment {
      padding: 0.8rem !important;
      margin: 0.5rem 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }

    /* Header responsive */
    .ui.header {
      font-size: 1.2rem !important;
    }

    .ui.header .sub.header {
      font-size: 0.8rem !important;
    }

    /* Button responsive */
    .ui.labeled.icon.button {
      width: 100% !important;
      margin-bottom: 0.5rem !important;
      padding: 0.75rem !important;
      font-size: 1rem !important;
    }

    /* Table responsive - stack on mobile */
    .ui.table {
      display: block !important;
      width: 100% !important;
      font-size: 0.75rem !important;
      border: none !important;
    }

    .ui.table thead {
      display: none !important;
    }

    .ui.table tbody {
      display: block !important;
      width: 100% !important;
    }

    .ui.table tr {
      display: block !important;
      width: 100% !important;
      border: 1px solid #ccc !important;
      margin-bottom: 0.5rem !important;
      padding: 0.8rem !important;
      background: #f9f9f9 !important;
      border-radius: 8px !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
    }

    .ui.table td {
      display: block !important;
      width: 100% !important;
      border: none !important;
      padding: 0.4rem 0 !important;
      text-align: center !important;
      white-space: normal !important;
      font-size: 0.8rem !important;
      position: relative !important;
    }

    .ui.table td:before {
      content: attr(data-label) ": " !important;
      font-weight: bold !important;
      color: #333 !important;
      display: inline-block !important;
      width: auto !important;
      margin-right: 0.5rem !important;
    }

    .ui.table td.numeric {
      text-align: center !important;
    }

    .ui.table td.center.aligned {
      text-align: center !important;
    }

    /* Improve button visibility on mobile */
    .ui.table .view-button {
      width: 100% !important;
      margin-top: 0.5rem !important;
      padding: 0.5rem !important;
      font-size: 0.9rem !important;
    }

    .ui.table .ui.small.label {
      font-size: 0.8rem !important;
      padding: 0.3rem 0.6rem !important;
    }

    /* Hide less important columns on mobile */
    .ui.table th:nth-child(4),
    .ui.table td:nth-child(4),
    .ui.table th:nth-child(5),
    .ui.table td:nth-child(5),
    .ui.table th:nth-child(6),
    .ui.table td:nth-child(6),
    .ui.table th:nth-child(7),
    .ui.table td:nth-child(7) {
      display: none !important;
    }

    /* Pagination responsive */
    .ui.pagination.menu {
      flex-wrap: wrap !important;
      justify-content: center !important;
      gap: 0.2rem !important;
    }

    .ui.pagination.menu .item {
      min-width: 2rem !important;
      padding: 0.3rem 0.4rem !important;
      font-size: 0.7rem !important;
    }

    .ui.tiny.text {
      font-size: 0.6rem !important;
      text-align: center !important;
    }

    /* Modal responsive */
    .modal-overlay {
      padding: 10px;
    }
    
    .ticket-modal {
      max-width: none;
      width: 100%;
      max-height: 95vh;
    }

    .ticket-modal .content {
      padding: 0.8rem;
    }

    .ticket-modal .user-info {
      padding: 0.6rem !important;
    }

    .ticket-modal .user-info .user-name {
      font-size: 1rem !important;
    }

    .ticket-modal .user-info .user-email {
      font-size: 0.8rem !important;
    }

    .ticket-modal .receipt-table td {
      padding: 0.3rem 0.4rem !important;
      font-size: 0.75rem !important;
    }

    .ticket-modal .promotion-item {
      padding: 0.5rem !important;
    }

    .ticket-modal .promotion-header {
      flex-direction: column !important;
      align-items: center !important;
      gap: 0.3rem !important;
    }

    .ticket-modal .promotion-amounts,
    .ticket-modal .promotion-dates {
      flex-direction: column !important;
      gap: 0.3rem !important;
    }

    .ticket-modal .promotion-amounts span,
    .ticket-modal .promotion-dates span {
      width: 100% !important;
    }

    .ticket-modal .ticket-actions {
      flex-direction: column !important;
      gap: 0.5rem !important;
    }

    .ticket-modal .ticket-actions .ui.button {
      width: 100% !important;
    }
  }

  /* Tablet styles */
  @media (min-width: 769px) and (max-width: 1024px) {
    .ui.container {
      padding: 1rem !important;
    }

    .ui.table {
      font-size: 0.8rem !important;
    }

    .ui.table th,
    .ui.table td {
      padding: 0.4rem 0.3rem !important;
    }

    /* Hide some columns on tablet */
    .ui.table th:nth-child(6),
    .ui.table td:nth-child(6),
    .ui.table th:nth-child(7),
    .ui.table td:nth-child(7) {
      display: none !important;
    }

    .ticket-modal {
      width: 90vw !important;
      max-width: 600px !important;
    }

    .ticket-modal .content {
      max-height: 65vh !important;
    }
  }

  /* Desktop styles */
  @media (min-width: 1025px) {
    .ui.table {
      font-size: 0.85rem !important;
    }

    .ui.table th,
    .ui.table td {
      padding: 0.5rem !important;
    }

    .ticket-modal {
      width: 80vw !important;
      max-width: 700px !important;
    }
  }

  /* Ensure content doesn't overlap with sidebar on desktop */
  @media (min-width: 769px) {
    .ui.container {
      margin-left: 0 !important;
      padding-left: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }

    .ui.segment {
      width: 100% !important;
      max-width: 100% !important;
      overflow-x: hidden !important;
    }
  }
</style>